import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import HeroCard from "./components/HeroSection/HeroCard";
import HeroesBar from "./components/HeroSection/HeroesBar";
import StreaksCard from "./components/Streaks/StreaksCard";
import SideBar from "./components/SideBar/SideBar";
import CreateQuest from "./components/CreateQuest/CreateQuest";
import QuestCard from "./components/QuestCard/QuestCard";
import LibraryCard from "./components/LibraryCard/LibraryCard";
import QuestCreationSuccessModal from "./components/QuestCreationSuccessModal/QuestCreationSuccessModal";

const QuestsPage = () => {
  const { username: urlUsername } = useParams();
  const username = urlUsername || localStorage.getItem("username");

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [userQuests, setUserQuests] = useState([]);
  const [publicQuests, setPublicQuests] = useState([]);
  const [hasMorePublicQuests, setHasMorePublicQuests] = useState(true);

  const [topUsers, setTopUsers] = useState([]);

  const [offset, setOffset] = useState(0);
  const LIMIT = 3;

  const [showUserQuests, setShowUserQuests] = useState(false);

  const [showCreationSuccess, setShowCreationSuccess] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch Public Quests
  useEffect(() => {
    const fetchPublicQuests = async () => {
      const username = localStorage.getItem("username");
      const token = localStorage.getItem("token");

      if (!username || !token) return;

      try {
        const res = await fetch(
          `${BACKEND_URL}/quests/public/${username}?limit=${LIMIT}&offset=0`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await res.json();

        if (res.ok) {
          console.log("📥 Public quests fetched:", data);
          setPublicQuests(data);
          setOffset(LIMIT); // ready for next batch
        } else {
          console.warn("⚠️ Failed to fetch public quests:", data.message);
        }
      } catch (err) {
        console.error("❌ Error fetching public quests:", err);
      }
    };

    fetchPublicQuests();
  }, [BACKEND_URL]);

  useEffect(() => {
    const fetchTopUsers = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/points/top?limit=3`);
        const data = await res.json();
        if (res.ok) {
          setTopUsers(data);
        } else {
          console.warn("⚠️ Failed to fetch top users:", data.message);
        }
      } catch (err) {
        console.error("❌ Error fetching top users:", err);
      }
    };

    fetchTopUsers();
    const interval = setInterval(fetchTopUsers, 30000);
    return () => clearInterval(interval);
  }, [BACKEND_URL]);

  const fetchUserQuests = async () => {
    const username = localStorage.getItem("username");
    const token = localStorage.getItem("token"); // Retrieve token

    if (!username || !token) return;

    try {
      const res = await fetch(`${BACKEND_URL}/quests/user/${username}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`, // Add token here
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();

      if (res.ok) {
        console.log("✅ User quests fetched:", data);
        setUserQuests(data);
        setShowUserQuests(true); // Trigger the toggle
      } else {
        console.warn("⚠️ Failed to fetch user quests:", data.message);
      }
    } catch (error) {
      console.error("❌ Error fetching user quests:", error);
    }
  };

  const loadMorePublicQuests = async () => {
    const username = localStorage.getItem("username");
    const token = localStorage.getItem("token");

    if (!username || !token || !hasMorePublicQuests) return;

    try {
      const res = await fetch(
        `${BACKEND_URL}/quests/public/${username}?limit=${LIMIT}&offset=${offset}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await res.json();

      if (res.ok) {
        if (data.length > 0) {
          setPublicQuests((prev) => [...prev, ...data]);
          setOffset((prevOffset) => prevOffset + LIMIT);
          if (data.length < LIMIT) {
            setHasMorePublicQuests(false); // No more to load
          }
        } else {
          setHasMorePublicQuests(false); // No more to load
          console.log("No more public quests to load.");
        }
      } else {
        console.warn("⚠️ Failed to fetch public quests:", data.message);
      }
    } catch (err) {
      console.error("❌ Error loading more public quests:", err);
    }
  };

  return (

    <>
      <div className="w-full min-h-screen bg-neutral-100 animate-fade-in px-4 pt-5 overflow-x-visible">
        {/* Hero Section */}
        <div className="w-full max-w-[1317px] mx-auto bg-white rounded-[20px] shadow-md px-4 py-6">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-black text-3xl sm:text-4xl md:text-5xl font-bold font-['Inter'] leading-tight">
              Quests
            </h1>
          </div>

          {/* Responsive Row */}
          <div className="w-full flex flex-col sm:flex-row items-center justify-center gap-6">
            {/* HeroesBar */}
            <div className="w-full sm:w-auto">
              <HeroesBar
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                topUsers={topUsers}
              />

            </div>

            {/* HeroCard */}
            <div className="w-full sm:w-auto">
              <HeroCard selectedIndex={selectedIndex} topUsers={topUsers} />
            </div>

            {/* StreaksCard */}
            <div className="w-full sm:w-auto">
              <StreaksCard />
            </div>
          </div>
        </div>
        {/* Below Hero Section */}
      <div className="w-full max-w-[1317px] mx-auto mt-6 px-1 sm:px-2">
        <div className="flex flex-col lg:flex-row gap-9 items-start">
          <SideBar />
          <div className="flex flex-col-reverse lg:flex-row gap-6 w-full">
            <div className="w-full lg:flex-1">
              <CreateQuest onQuestCreated={() => setShowCreationSuccess(true)} />
              {(showUserQuests ? userQuests : publicQuests).length > 0 ? (
                <div className="mt-8 flex flex-col items-center space-y-6">
                  {(showUserQuests ? userQuests : publicQuests).map((quest, idx) => (
                    <QuestCard key={quest.questId || idx} quest={quest} isOwned={showUserQuests } />
                  ))}
                  {!showUserQuests && hasMorePublicQuests ? (
                    <button
                      onClick={loadMorePublicQuests}
                      className="mt-4 px-4 py-2 text-sm rounded-full border border-black hover:bg-black hover:text-white transition-all"
                    >
                      View Older Quests
                    </button>
                  ) : (
                    <p className="mt-4 text-sm text-gray-500 italic">No more public quests to load.</p>
                  )}
                </div>
              ) : (
                <div className="mt-8 text-center text-sm text-gray-500 italic">
                  {showUserQuests
                    ? "You haven’t created any quests yet."
                    : "No public quests available right now."}
                </div>
              )}
            </div>

            <div className="w-full lg:w-[18rem] shrink-0">
              <LibraryCard
                showUserQuests={showUserQuests}
                setShowUserQuests={setShowUserQuests}
                fetchUserQuests={fetchUserQuests}
              />
            </div>
          </div>

        </div>
      </div>
      </div>

      <QuestCreationSuccessModal
        isOpen={showCreationSuccess}
        onClose={() => setShowCreationSuccess(false)}
      />
    </>
    
  );
};

export default QuestsPage;